import { useEffect, useState } from 'react'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { EsqueletoDetalle } from '@/shared/components/Esqueleto'
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage'
import { usePermissions } from '@/shared/hooks/usePermissions'
import { RH } from '@/constants/permissions'
import { AltaManualDeCandidato } from '@/features/rh/components/AltaManualDeCandidato'
import { apiService, type ApprovedJobRequest } from '@/services/api'
import { mensajeDeError } from '@/shared/lib/mensajeDeError'

/** A dónde se vuelve: el alta siempre sale del panel. */
const PANEL = '/rh-dashboard'

/**
 * Alta manual de un candidato. Antes era un modal dentro del panel de
 * reclutamiento; es una pantalla porque es la regla de la auditoría —si es una
 * pantalla, es una ruta— y porque el formulario creció de más para una caja
 * flotante.
 *
 * Al ser ruta propia ahora se llega escribiendo la URL, así que el permiso se
 * revisa aquí: en el modal bastaba con esconder el botón.
 */
export const NuevoCandidato = () => {
  const navigate = useNavigate()
  const { hasPermission, isLoading: cargandoPermisos } = usePermissions()
  const [vacantes, setVacantes] = useState<ApprovedJobRequest[]>([])
  const [cargandoVacantes, setCargandoVacantes] = useState(true)
  const [error, setError] = useState('')

  // El permiso no viene en ningun rol: se otorga uno por uno.
  const puedeDarDeAlta = hasPermission(RH.RECLUTAMIENTO_ALTA_MANUAL)

  useEffect(() => {
    // Los permisos llegan asíncronos y mientras tanto hasPermission dice que no
    // a todo: pedir las vacantes antes de que lleguen daría un 403 de adorno.
    if (cargandoPermisos) return
    if (!puedeDarDeAlta) {
      setCargandoVacantes(false)
      return
    }

    let vigente = true
    apiService
      .getApprovedJobRequests()
      .then((jobs) => vigente && setVacantes(jobs))
      .catch((errorCrudo: unknown) => vigente && setError(mensajeDeError(errorCrudo)))
      .finally(() => vigente && setCargandoVacantes(false))
    return () => {
      vigente = false
    }
  }, [cargandoPermisos, puedeDarDeAlta])

  if (!cargandoPermisos && !puedeDarDeAlta) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    )
  }

  const volver = () => navigate(PANEL)

  return (
    // Mismo marco que el alta de herramientas en almacén: centrado y ancho, no
    // una columna angosta pegada a la izquierda. El layout no trae padding
    // propio, lo pone cada pantalla.
    <div className="mx-auto px-6 py-8 max-w-7xl">
      <div className="flex items-start gap-3 mb-6">
        <button
          onClick={volver}
          className={estiloDeBoton('secundario')}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div>
          <TituloDePantalla nivel="interna">Nuevo candidato</TituloDePantalla>
          <p className="text-sm text-[#787878]">
            Panel de candidatos / Registrar candidato
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {cargandoPermisos || cargandoVacantes ? (
        <EsqueletoDetalle campos={8} />
      ) : (
        <AltaManualDeCandidato
          vacantes={vacantes}
          onCancelar={volver}
          onRegistrado={() =>
            // El panel se encarga del aviso: aquí la pantalla ya se fue.
            navigate(PANEL, { state: { candidatoRegistrado: true } })
          }
        />
      )}
    </div>
  )
}

export default NuevoCandidato
